"use client";
import { Box, Typography, Button } from "@mui/material";
import {
  EmojiEvents,
  ChatBubble,
  CheckCircle,
  Restaurant,
  Star,
} from "@mui/icons-material";
import React from "react";
import { BicepsFlexed, User } from "lucide-react";
import PersonIcon from "@mui/icons-material/Person";

interface ClientAwardSlideProps {
  award: {
    type: string;
    title: string;
    clientName: string;
    displayName: string;
    clientAvatar: string;
    metric: string;
    message?: string;
    comment?: string;
    cta: string;
    breakdown?: string;
    beforeWeight?: string;
    afterWeight?: string;
  };
  showCta?: boolean;
  awardIndex: number;
  onCtaClick?: (award: any) => void;
}

export default function ClientAwardSlide({
  award,
  awardIndex,
  showCta = true,
  onCtaClick,
}: ClientAwardSlideProps) {
  const getIcon = () => {
    switch (award.type) {
      case "mostChatty":
        return <ChatBubble sx={{ fontSize: 80 }} />;
      case "mostKgLifted":
        return <BicepsFlexed size={80} />;
      case "mostCompletions":
        return <CheckCircle sx={{ fontSize: 80 }} />;
      case "bestNutrition":
        return <Restaurant sx={{ fontSize: 80 }} />;
      case "bestTransformation":
        return <Star sx={{ fontSize: 80 }} />;
      default:
        return <EmojiEvents sx={{ fontSize: 80 }} />;
    }
  };

  const getGradient = () => {
    switch (award.type) {
      case "mostChatty":
        return "linear-gradient(135deg, #ec4899 0%, #06b6d4 50%)";
      case "mostKgLifted":
        return "linear-gradient(135deg, #f59e0b 0%, #ef4444 50%)";
      case "mostCompletions":
        return "linear-gradient(135deg, #8b5cf6 0%, #ec4899 50%)";
      case "bestNutrition":
        return "linear-gradient(135deg, #10b981 0%, #06b6d4 50%)";
      case "bestTransformation":
        return "linear-gradient(135deg, #ec4899 0%, #f59e0b 50%)";
      default:
        return "linear-gradient(135deg, #ec4899 0%, #06b6d4 50%)";
    }
  };
  const handleCtaClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (onCtaClick) {
      e.stopPropagation();
      onCtaClick(award);
    }
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: { xs: "center", md: "space-between" },
        background: "#0a0a0a",
        position: "relative",
        color: "white",
        p: 4,
        pt: { xs: 4 },
        textAlign: "center",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: getGradient(),
          opacity: 0.15,
          pointerEvents: "none",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          flex: { xs: "none", md: 1 },
          position: "relative",
          zIndex: 1,
        }}
      >
        <Box
          sx={{
            mb: 0,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            animation: "bounce 2s ease-in-out infinite",
            "@keyframes bounce": {
              "0%, 100%": { transform: "translateY(0) scale(1)" },
              "50%": { transform: "translateY(-20px) scale(1.05)" },
            },
          }}
        >
          {getIcon()}
        </Box>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            mb: { xs: 2, md: 3 },
            animation: "fadeIn 0.5s ease-out 0.2s both",
            "@keyframes fadeIn": {
              "0%": { opacity: 0, transform: "translateY(-10px)" },
              "100%": { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          {award.title}
        </Typography>
        <Box
          sx={{
            bgcolor: "rgba(255, 255, 255, 0.05)",
            border: "2px solid rgba(255, 255, 255, 0.1)",
            borderRadius: 4,
            p: 3,
            mb: { xs: 4, md: 3 },
            width: "fit-content",
            minWidth: { xs: 280, sm: 320 },
            maxWidth: { xs: "90vw", sm: 400 },
            animation:
              "scaleIn 0.6s cubic-bezier(0.68, -0.55, 0.265, 1.55) 0.4s both",
            "@keyframes scaleIn": {
              "0%": { transform: "scale(0) rotate(-5deg)", opacity: 0 },
              "100%": { transform: "scale(1) rotate(0deg)", opacity: 1 },
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              mb: 2,
            }}
          >
            <Box
              sx={{
                background: getGradient(),
                border: `4px solid transparent`,
                padding: "4px",
                borderRadius: "4px",

                height: "100px",
                width: "100px",
              }}
            >
              <Box
                sx={{
                  borderRadius: "50%",
                  margin: "0 auto",
                  overflow: "hidden",
                  display: "flex",
                  height: "100%",
                  width: "100%",
                  alignItems: "center",
                  justifyContent: "center",
                  position: "relative",
                }}
              >
                {award.clientAvatar && award.clientAvatar != "null" ? (
                  <img
                    src={award.clientAvatar}
                    alt={award.displayName}
                    width="100%"
                  />
                ) : (
                  <PersonIcon sx={{ width: 60, height: 60 }} />
                )}
              </Box>
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              {award.displayName}
            </Typography>
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 600, opacity: 0.9 }}>
            {award.metric}
          </Typography>
          {award.breakdown && (
            <Typography variant="body2" sx={{ mt: 1, opacity: 0.7 }}>
              {award.breakdown}
            </Typography>
          )}
          {award.beforeWeight && award.afterWeight && (
            <Box
              sx={{ mt: 2, display: "flex", justifyContent: "center", gap: 2 }}
            >
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.6 }}>
                  Before
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {award.beforeWeight}
                </Typography>
              </Box>
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, mt: 1, opacity: 0.7 }}
              >
                →
              </Typography>
              <Box>
                <Typography variant="caption" sx={{ opacity: 0.6 }}>
                  After
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {award.afterWeight}
                </Typography>
              </Box>
            </Box>
          )}
        </Box>
        {award.comment && (
          <Typography
            variant="body1"
            sx={{
              maxWidth: 500,
              opacity: 0.8,
              mb: { xs: 6, md: 0 },
              animation: "fadeIn 0.5s ease-out 0.8s both",
            }}
          >
            {award.comment}
          </Typography>
        )}
      </Box>

      {showCta && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: { xs: 6, md: 2 },
            pb: { xs: 4, md: 2 },
            position: "relative",
            zIndex: 1,
          }}
        >
          {award.message && (
            <Typography
              variant="body1"
              sx={{
                maxWidth: 500,
                opacity: 0.8,
                animation: "fadeIn 0.5s ease-out 0.8s both",
              }}
            >
              {award.message}
            </Typography>
          )}
          <Button
            variant="contained"
            sx={{
              background: getGradient(),
              color: "white",
              px: 4,
              py: 1.5,
              borderRadius: 3,
              fontWeight: 700,
              zIndex: 10,
              animation:
                "popIn 0.5s cubic-bezier(0.68, -0.55, 0.265, 1.55) 1s both",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                transform: "scale(1.05) translateY(-2px)",
                boxShadow: "0 8px 20px rgba(236, 72, 153, 0.4)",
              },
              "@keyframes popIn": {
                "0%": { transform: "scale(0)", opacity: 0 },
                "100%": { transform: "scale(1)", opacity: 1 },
              },
            }}
            onClick={handleCtaClick}
          >
            {award.cta}
          </Button>
        </Box>
      )}
    </Box>
  );
}
